import type { DocumentSetHint } from "@loumy/contracts";
import type { ZodType } from "zod";
import { joinPath, UnsafePathError } from "./paths";

// D72 — reine Helfer des Multi-Dokument-Hosts: Abbildung zwischen Dokument-NAMEN (UI) und
// Projekt-PFADEN (ProjectSource) + (De-)Serialisierung eines TDoc als JSON-Bytes. Engine-/editor-
// neutral; das editor-typ-spezifische Substantiv/Verzeichnis/Endung kommt aus dem DocumentSetHint.

/** Geworfen bei einem Dokument-Namen, der kein einzelnes sicheres Pfad-Segment ist. */
export class InvalidDocumentNameError extends Error {
  constructor(readonly docName: string) {
    super(`Ungültiger Dokument-Name '${docName}': nur Buchstaben/Ziffern/-/_/Leerzeichen, keine Pfad-Trenner.`);
    this.name = "InvalidDocumentNameError";
  }
}

// Ein Dokument-Name ist ein EINZELNES Segment (kein Verzeichnis): keine Slashes/Backslashes,
// kein `..`, kein führender Punkt. Bewusst restriktiv (Whitelist) — der Name wird zu einem Pfad.
const VALID_NAME = /^[\p{L}\p{N} _-]+$/u;

export function isValidDocumentName(name: string): boolean {
  return typeof name === "string" && name.length > 0 && name.length <= 120 && VALID_NAME.test(name) && !name.includes("..");
}

export function assertValidDocumentName(name: string): string {
  if (!isValidDocumentName(name)) throw new InvalidDocumentNameError(String(name));
  return name;
}

/** Glob, der alle Dokumente eines Sets im Projekt findet (`levels/*.level.json`). */
export function documentGlob(set: DocumentSetHint): string {
  return `${set.dir}/*${set.extension}`;
}

/** Projekt-relativer (sandboxed) Pfad eines Dokuments aus seinem Namen. */
export function documentPath(set: DocumentSetHint, name: string): string {
  return joinPath(set.dir, `${assertValidDocumentName(name)}${set.extension}`);
}

/**
 * Dokument-Name aus einem Projekt-Pfad (Umkehrung von `documentPath`). Liefert `null`, wenn der
 * Pfad nicht ins Set gehört (falsches Verzeichnis/Endung) oder kein gültiger Name resultiert —
 * so filtert der Host fremde Treffer robust heraus.
 */
export function documentNameOf(set: DocumentSetHint, path: string): string | null {
  let normalized: string;
  try {
    normalized = joinPath(path); // normalisiert + sandboxed
  } catch (e) {
    if (e instanceof UnsafePathError) return null;
    throw e;
  }
  const prefix = set.dir.length > 0 ? `${set.dir}/` : "";
  if (!normalized.startsWith(prefix)) return null;
  if (!normalized.endsWith(set.extension)) return null;
  const rest = normalized.slice(prefix.length, normalized.length - set.extension.length);
  // Kein verschachteltes Unterverzeichnis (das Set ist flach) und ein gültiger Name.
  if (rest.includes("/") || !isValidDocumentName(rest)) return null;
  return rest;
}

/** Serialisiert ein TDoc als hübsch eingerücktes JSON (UTF-8-Bytes) für `ProjectSource.writeFile`. */
export function serializeDocument(doc: unknown): Uint8Array {
  return new TextEncoder().encode(`${JSON.stringify(doc, null, 2)}\n`);
}

/**
 * Parst gelesene Bytes zu einem validierten TDoc über das `documentSchema` des Editors (D12,
 * fail-closed). Wirft bei kaputtem JSON oder Schema-Verstoß — der Host meldet das als Fehler,
 * statt ungültige Daten in den Store zu lassen.
 */
export function parseDocument<T>(bytes: Uint8Array, schema: ZodType<T>): T {
  const text = new TextDecoder().decode(bytes);
  const json: unknown = JSON.parse(text);
  return schema.parse(json);
}
